import { hex } from 'zod/v4';

export function hsbaToRgba({
    hue,
    saturation,
    brightness,
    alpha,
}: {
    hue: number;
    saturation: number;
    brightness: number;
    alpha: number;
}): string {
    const c = brightness * saturation; // Chroma
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = brightness - c;

    let r1 = 0,
        g1 = 0,
        b1 = 0;

    if (0 <= hue && hue < 60) {
        r1 = c;
        g1 = x;
        b1 = 0;
    } else if (60 <= hue && hue < 120) {
        r1 = x;
        g1 = c;
        b1 = 0;
    } else if (120 <= hue && hue < 180) {
        r1 = 0;
        g1 = c;
        b1 = x;
    } else if (180 <= hue && hue < 240) {
        r1 = 0;
        g1 = x;
        b1 = c;
    } else if (240 <= hue && hue < 300) {
        r1 = x;
        g1 = 0;
        b1 = c;
    } else if (300 <= hue && hue < 360) {
        r1 = c;
        g1 = 0;
        b1 = x;
    }

    // Convert to [0, 255]
    const r = Math.round((r1 + m) * 255);
    const g = Math.round((g1 + m) * 255);
    const bFinal = Math.round((b1 + m) * 255);

    return `rgba(${r}, ${g}, ${bFinal}, ${alpha})`;
}

export function rgbaToHsba(rgbaColor: string): { hue: number; saturation: number; brightness: number; alpha: number } {
    const rgbaMatch = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(rgbaColor);

    if (!rgbaMatch) {
        throw new Error('Invalid RGBA color format');
    }

    const r = parseInt(rgbaMatch[1] as string, 10);
    const g = parseInt(rgbaMatch[2] as string, 10);
    const b = parseInt(rgbaMatch[3] as string, 10);
    const alpha = rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1;
    // Normalize RGB to [0, 1]
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    let hue = 0;
    let saturation = 0;
    const bOut = max; // Brightness = max of normalized RGB

    // Hue calculation
    if (delta !== 0) {
        if (max === rNorm) {
            hue = 60 * (((gNorm - bNorm) / delta) % 6);
        } else if (max === gNorm) {
            hue = 60 * ((bNorm - rNorm) / delta + 2);
        } else {
            hue = 60 * ((rNorm - gNorm) / delta + 4);
        }
    }

    if (hue < 0) hue += 360;

    // Saturation calculation
    saturation = max === 0 ? 0 : delta / max;

    return { hue, saturation, brightness: bOut, alpha };
}

export function hsbaToHex({
    hue,
    saturation,
    brightness,
    alpha,
}: {
    hue: number;
    saturation: number;
    brightness: number;
    alpha?: number;
}): string {
    const c = brightness * saturation; // Chroma
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = brightness - c;

    let r1 = 0,
        g1 = 0,
        b1 = 0;

    if (0 <= hue && hue < 60) {
        r1 = c;
        g1 = x;
        b1 = 0;
    } else if (60 <= hue && hue < 120) {
        r1 = x;
        g1 = c;
        b1 = 0;
    } else if (120 <= hue && hue < 180) {
        r1 = 0;
        g1 = c;
        b1 = x;
    } else if (180 <= hue && hue < 240) {
        r1 = 0;
        g1 = x;
        b1 = c;
    } else if (240 <= hue && hue < 300) {
        r1 = x;
        g1 = 0;
        b1 = c;
    } else if (300 <= hue && hue < 360) {
        r1 = c;
        g1 = 0;
        b1 = x;
    }

    // Convert to [0, 255]
    const r = Math.round((r1 + m) * 255);
    const g = Math.round((g1 + m) * 255);
    const bFinal = Math.round((b1 + m) * 255);
    const a = alpha !== undefined ? Math.round(alpha * 255) : 255;

    // Convert to hex and ensure two digits
    const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase();

    return a < 255 ? `#${toHex(r)}${toHex(g)}${toHex(bFinal)}${toHex(a)}` : `#${toHex(r)}${toHex(g)}${toHex(bFinal)}`;
}

export function hexToHsba(hexColor: string): { hue: number; saturation: number; brightness: number; alpha: number } {
    // Remove the leading '#' if present
    if (hexColor.startsWith('#')) {
        hexColor = hexColor.slice(1);
    }

    // Handle shorthand hex (e.g., #03F)
    if (hexColor.length === 3) {
        hexColor = hexColor
            .split('')
            .map(char => char + char)
            .join('');
    }

    if (hexColor.length !== 6 && hexColor.length !== 8) {
        throw new Error('Invalid HEX color format');
    }

    const r = parseInt(hexColor.slice(0, 2), 16);
    const g = parseInt(hexColor.slice(2, 4), 16);
    const b = parseInt(hexColor.slice(4, 6), 16);
    const alpha = hexColor.length === 8 ? parseInt(hexColor.slice(6, 8), 16) / 255 : 1;

    // Normalize RGB to [0, 1]
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    let hue = 0;
    let saturation = 0;
    const brightness = max; // Brightness = max of normalized RGB

    // Hue calculation
    if (delta !== 0) {
        if (max === rNorm) {
            hue = 60 * (((gNorm - bNorm) / delta) % 6);
        } else if (max === gNorm) {
            hue = 60 * ((bNorm - rNorm) / delta + 2);
        } else {
            hue = 60 * ((rNorm - gNorm) / delta + 4);
        }
    }

    if (hue < 0) hue += 360;

    // Saturation calculation
    saturation = max === 0 ? 0 : delta / max;

    return { hue, saturation, brightness, alpha };
}
